import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Gift, X, Sparkles, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Máscaras ──────────────────────────────────────────────────────────────────

const applyTelefoneMask = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

// CNPJ: suporta alfanumérico (nova regra Receita 2026) e numérico
const applyCNPJMask = (value) => {
  // Mantém letras maiúsculas + dígitos, remove o resto
  const raw = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
  const isAlpha = /[A-Z]/.test(raw);

  if (isAlpha) {
    // Alfanumérico: XX.XXX.XXX/XXXX-XX sem converter
    return raw
      .replace(/^(.{2})(.+)/, '$1.$2')
      .replace(/^(.{6})(.+)/, '$1.$2')
      .replace(/^(.{10})(.+)/, '$1/$2')
      .replace(/^(.{15})(.+)/, '$1-$2');
  }
  // Numérico puro
  return raw
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d)/, '$1-$2');
};

const applyCPFMask = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3}\.\d{3}\.\d{3})(\d)/, '$1-$2');
};

// ── Validações ────────────────────────────────────────────────────────────────

const validarCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(digits[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  return rest === parseInt(digits[10]);
};

const validarCNPJ = (cnpj) => {
  const raw = cnpj.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (raw.length !== 14) return false;

  const isAlpha = /[A-Z]/.test(raw);

  if (isAlpha) {
    // CNPJ alfanumérico — valida apenas comprimento (dígito verificador ainda em regulamentação)
    return true;
  }

  // CNPJ numérico — valida dígitos verificadores
  const digits = raw;
  if (/^(\d)\1{13}$/.test(digits)) return false;

  const calc = (d, weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) sum += parseInt(d[i]) * weights[i];
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const d1 = calc(digits, w1);
  const d2 = calc(digits, w2);

  return d1 === parseInt(digits[12]) && d2 === parseInt(digits[13]);
};

const nomeValido = (value) => /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.&-]+$/.test(value.trim());

// ── Componente ────────────────────────────────────────────────────────────────

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isBusiness = searchParams.get('mode') === 'business';
  const upgradeParam = searchParams.get('upgrade'); // 'pro' se vier da landing

  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);
  const [showCupomModal, setShowCupomModal] = useState(false);
  const [cupomEncontrado, setCupomEncontrado] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profissoes, setProfissoes] = useState([]); // NOVO: busca do banco

  // Buscar profissões do banco ao carregar
  useEffect(() => {
    const fetchProfissoes = async () => {
      try {
        const res = await axios.get(`${API}/profissoes`);
        const lista = res.data?.profissoes || res.data || [];
        // Extrair só os nomes e ordenar alfabeticamente
        const nomes = lista
          .map(p => p.nome || p)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, 'pt-BR'));
        setProfissoes(nomes);
      } catch (err) {
        console.error('Erro ao buscar profissões:', err);
        // Fallback com lista básica se API falhar
        setProfissoes([
          'Advogado', 'Contador', 'Corretor de Imóveis', 'Corretor de Seguros',
          'Dentista', 'Esteticista', 'Manicure/Pedicure', 'Mecânico',
          'Nutricionista', 'Personal Trainer', 'Veterinário', 'Outros'
        ]);
      }
    };
    fetchProfissoes();
  }, []);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome_empresa: '',
    cnpj: '',
    cpf: '',
    tipo_profissao: '',
    telefone: '',
    cupom_codigo: '',
    aceitaTermos: false
  });

  // Consulta CNPJ na BrasilAPI e busca cupom por CNAE
  const consultarCNPJ = useCallback(async (cnpj) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return;
    
    // Só consulta CNPJ numérico (alfanumérico não é suportado pela BrasilAPI)
    if (/[A-Z]/i.test(cnpj.replace(/[\.\-\/]/g, ''))) return;

    setCnpjLoading(true);
    try {
      // 1. Consultar BrasilAPI
      const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`, {
        timeout: 8000
      });
      
      const data = response.data;
      setCnpjData(data);
      
      // Preencher nome da empresa se estiver vazio
      if (!formData.nome_empresa && data.razao_social) {
        setFormData(prev => ({ 
          ...prev, 
          nome_empresa: data.nome_fantasia || data.razao_social 
        }));
      }

      // Preencher telefone se estiver vazio
      if (!formData.telefone && data.ddd_telefone_1) {
        const telefone = `(${data.ddd_telefone_1.substring(0, 2)}) ${data.ddd_telefone_1.substring(2)}`;
        setFormData(prev => ({ ...prev, telefone }));
      }

      // 2. Buscar cupom por CNAE no backend
      if (data.cnae_fiscal) {
        try {
          const cupomResponse = await axios.get(
            `${API}/cupons/por-cnae/${data.cnae_fiscal}`
          );
          
          if (cupomResponse.data && cupomResponse.data.codigo) {
            const cupom = cupomResponse.data;
            setCupomEncontrado(cupom);
            setFormData(prev => ({ ...prev, cupom_codigo: cupom.codigo }));
            setShowCupomModal(true);
          }
        } catch (e) {
          // Sem cupom para este CNAE, tudo bem
          console.log('Nenhum cupom encontrado para CNAE:', data.cnae_fiscal);
        }
      }
    } catch (error) {
      // Não mostra erro, apenas não preenche
      console.log('CNPJ não encontrado ou erro na consulta');
    } finally {
      setCnpjLoading(false);
    }
  }, [formData.nome_empresa, formData.telefone]);

  // Debounce para consultar CNPJ
  useEffect(() => {
    if (!isLogin && isBusiness && formData.cnpj) {
      const cnpjLimpo = formData.cnpj.replace(/\D/g, '');
      if (cnpjLimpo.length === 14) {
        const timer = setTimeout(() => {
          consultarCNPJ(formData.cnpj);
        }, 800); // Espera 800ms após parar de digitar
        return () => clearTimeout(timer);
      }
    }
  }, [formData.cnpj, isLogin, isBusiness, consultarCNPJ]);

  const handleChange = (field, value) => {
    let masked = value;

    if (field === 'telefone') masked = applyTelefoneMask(value);
    if (field === 'cnpj')    masked = applyCNPJMask(value);
    if (field === 'cpf')     masked = applyCPFMask(value);
    if (field === 'cupom_codigo') masked = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    setFormData(prev => ({ ...prev, [field]: masked }));
  };

  const validarFormulario = () => {
    if (!isLogin) {
      // Nome obrigatório
      if (!formData.nome_empresa.trim()) {
        toast.error('Informe o nome' + (isBusiness ? ' da empresa' : ' completo'));
        return false;
      }
      if (!nomeValido(formData.nome_empresa)) {
        toast.error('O nome contém caracteres inválidos');
        return false;
      }

      // Telefone
      const foneDigits = formData.telefone.replace(/\D/g, '');
      if (foneDigits.length < 10) {
        toast.error('Informe um telefone válido com DDD');
        return false;
      }

      if (isBusiness) {
        // CNPJ
        if (!formData.cnpj) {
          toast.error('Informe o CNPJ');
          return false;
        }
        if (!validarCNPJ(formData.cnpj)) {
          toast.error('CNPJ inválido. Verifique os números informados.');
          return false;
        }
        if (!formData.tipo_profissao) {
          toast.error('Selecione o tipo de profissão');
          return false;
        }
      } else {
        // CPF para PF
        if (!formData.cpf) {
          toast.error('Informe o CPF');
          return false;
        }
        if (!validarCPF(formData.cpf)) {
          toast.error('CPF inválido. Verifique os números informados.');
          return false;
        }
      }

      // Confirmar senha
      if (formData.password !== confirmPassword) {
        toast.error('As senhas não coincidem');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('A senha deve ter pelo menos 6 caracteres');
        return false;
      }

      // Validar aceite dos termos
      if (!formData.aceitaTermos) {
        toast.error('Você precisa aceitar os Termos de Uso e Política de Privacidade');
        return false;
      }
    }
    return true;
  };

  // 🔥 NOVA FUNÇÃO: Criar sessão de checkout Stripe após registro
  const createCheckoutSession = async (token) => {
    try {
      const response = await axios.post(
        `${API}/payments/create-checkout`, // 🔥 CORRIGIDO: endpoint correto
        {}, // Backend não espera body, usa dados do usuário autenticado
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Backend retorna checkout_url
      if (response.data?.checkout_url) {
        // Redirecionar para o Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de checkout não recebida');
      }
    } catch (err) {
      toast.error(`Erro ao processar upgrade: ${err.response?.data?.detail || err.message}`);
      // Em caso de erro, deixa ir pro Dashboard mesmo após 2 segundos
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';

      let payload;
      if (isLogin) {
        payload = { email: formData.email, password: formData.password };
      } else {
        payload = {
          email: formData.email,
          password: formData.password,
          nome_empresa: formData.nome_empresa,
          cnpj: isBusiness ? formData.cnpj : formData.cpf,
          tipo_profissao: formData.tipo_profissao,
          telefone: formData.telefone,
          tipo_usuario: isBusiness ? 'PJ' : 'PF',
          cupom_codigo: formData.cupom_codigo || null,
        };
      }

      const response = await axios.post(`${API}${endpoint}`, payload);

      localStorage.setItem('lembrou_token', response.data.token);
      localStorage.setItem('lembrou_user', JSON.stringify(response.data.user));

      // Mensagem de sucesso personalizada se usou cupom
      if (!isLogin && response.data.cupom_aplicado) {
        toast.success(`Conta criada! Cupom ${response.data.cupom_aplicado.codigo} aplicado com ${response.data.cupom_aplicado.desconto}% de desconto!`);
      } else {
        toast.success(isLogin ? 'Login realizado!' : 'Conta criada com sucesso!');
      }

      // 🔥 FLUXO DE UPGRADE PRO
      if (!isLogin && isBusiness && upgradeParam === 'pro') {
        await createCheckoutSession(response.data.token);
        return;
      }

      // Fluxo normal
      const userType = response.data.user?.tipo_usuario;
      if (!isLogin) {
        // Cadastro → página de sucesso para rastrear conversão
        navigate(`/cadastro-sucesso?tipo=${userType || 'PJ'}`);
      } else if (userType === 'PF') {
        navigate('/timeline');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar requisição');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 rounded-3xl shadow-xl border-0 relative">

        <button
          data-testid="back-to-home"
          onClick={() => navigate('/')}
          className="absolute top-6 left-6 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>

        <div className="text-center mb-8">
          <img src="/icon-lembrou.jpg" alt="Lembrou?" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
          <h1 className="text-3xl font-bold text-gray-900">
            {isLogin ? 'Bem-vindo de volta' : 'Criar conta'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isBusiness ? 'Gestão de Lembretes' : 'Timeline Pessoal'}
          </p>
          
          {/* Aviso de Upgrade PRO */}
          {!isLogin && isBusiness && upgradeParam === 'pro' && (
            <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-3">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-800 font-medium">
                  Você será direcionado para o pagamento do Plano PRO após o cadastro
                </p>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── Campos de cadastro ── */}
          {!isLogin && (
            <>
              <div>
                <Label htmlFor="nome_empresa">
                  {isBusiness ? 'Nome da Empresa' : 'Nome Completo'}
                </Label>
                <Input
                  id="nome_empresa"
                  data-testid="auth-empresa-input"
                  placeholder={isBusiness ? 'Minha Empresa Ltda' : 'Seu nome completo'}
                  value={formData.nome_empresa}
                  onChange={(e) => handleChange('nome_empresa', e.target.value)}
                  required
                  className="rounded-xl h-11"
                />
              </div>

              {isBusiness ? (
                <>
                  <div>
                    <Label htmlFor="cnpj">
                      CNPJ
                      <span className="ml-1 text-xs font-normal text-gray-400">(numérico ou alfanumérico)</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="cnpj"
                        data-testid="auth-cnpj-input"
                        placeholder="00.000.000/0000-00"
                        value={formData.cnpj}
                        onChange={(e) => handleChange('cnpj', e.target.value)}
                        required
                        className="rounded-xl h-11 font-mono tracking-wide pr-10"
                      />
                      {cnpjLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        </div>
                      )}
                    </div>
                    {cnpjData && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {cnpjData.nome_fantasia || cnpjData.razao_social}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="tipo_profissao">Tipo de Profissão</Label>
                    <select
                      id="tipo_profissao"
                      data-testid="auth-profissao-select"
                      value={formData.tipo_profissao}
                      onChange={(e) => handleChange('tipo_profissao', e.target.value)}
                      required
                      className="w-full h-11 px-3 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-red-100 outline-none"
                    >
                      <option value="">Selecione...</option>
                      {profissoes.length === 0 ? (
                        <option disabled>Carregando...</option>
                      ) : (
                        profissoes.map(prof => (
                          <option key={prof} value={prof}>{prof}</option>
                        ))
                      )}
                    </select>
                  </div>
                </>
              ) : (
                <div>
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    data-testid="auth-cpf-input"
                    placeholder="000.000.000-00"
                    value={formData.cpf}
                    onChange={(e) => handleChange('cpf', e.target.value)}
                    required
                    className="rounded-xl h-11 font-mono tracking-wide"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  data-testid="auth-telefone-input"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  required
                  className="rounded-xl h-11"
                />
              </div>
            </>
          )}

          {/* ── Email e senha ── */}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="auth-email-input"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className="rounded-xl h-11"
            />
          </div>

          <div>
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="auth-password-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                className="rounded-xl h-11 pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirmar senha — apenas no cadastro */}
          {!isLogin && (
            <div>
              <Label htmlFor="confirm_password">Confirmar Senha</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  data-testid="auth-confirm-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className={`rounded-xl h-11 pr-11 ${
                    confirmPassword && confirmPassword !== formData.password
                      ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
                      : confirmPassword && confirmPassword === formData.password
                      ? 'border-green-400 focus:border-green-400 focus:ring-green-100'
                      : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== formData.password && (
                <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
              )}
              {confirmPassword && confirmPassword === formData.password && (
                <p className="text-xs text-green-600 mt-1">✓ Senhas coincidem</p>
              )}
            </div>
          )}

          {/* Campo de Cupom (apenas no cadastro) */}
          {!isLogin && (
            <div>
              <Label htmlFor="cupom">Cupom de Desconto (opcional)</Label>
              <Input
                id="cupom"
                data-testid="auth-cupom-input"
                type="text"
                placeholder="Digite o código do cupom"
                value={formData.cupom_codigo}
                onChange={(e) => handleChange('cupom_codigo', e.target.value)}
                className="rounded-xl h-11 font-mono uppercase"
                maxLength={20}
              />
              <p className="text-xs text-gray-400 mt-1">
                Se você tem um cupom de desconto, digite aqui
              </p>
            </div>
          )}

          {/* Checkbox Termos e Privacidade (apenas no cadastro) */}
          {!isLogin && (
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <input
                type="checkbox"
                id="aceitaTermos"
                data-testid="auth-termos-checkbox"
                checked={formData.aceitaTermos}
                onChange={(e) => setFormData(prev => ({ ...prev, aceitaTermos: e.target.checked }))}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
              />
              <label htmlFor="aceitaTermos" className="text-sm text-gray-600 cursor-pointer select-none">
                Li e concordo com os{' '}
                <Link to="/termos" target="_blank" className="text-primary hover:underline font-medium">
                  Termos de Uso
                </Link>
                {' '}e a{' '}
                <Link to="/privacidade" target="_blank" className="text-primary hover:underline font-medium">
                  Política de Privacidade
                </Link>
              </label>
            </div>
          )}

          <Button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full rounded-2xl h-11 bg-primary hover:bg-primary-hover text-white font-medium shadow-md"
          >
            {loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}
          </Button>
        </form>

        {isLogin && (
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-gray-600 hover:text-primary transition-colors"
            >
              Esqueceu a senha?
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            data-testid="auth-toggle-button"
            onClick={() => { setIsLogin(!isLogin); setConfirmPassword(''); setShowPassword(false); setShowConfirmPassword(false); }}
            className="text-sm text-gray-600 hover:text-primary transition-colors"
          >
            {isLogin ? 'Não tem conta? Criar agora' : 'Já tem conta? Fazer login'}
          </button>
        </div>

        <button
          data-testid="back-to-home-button"
          onClick={() => navigate('/')}
          className="mt-4 text-sm text-gray-500 hover:text-gray-700 w-full text-center"
        >
          ← Voltar para início
        </button>
      </Card>

      {/* Modal de Cupom Encontrado */}
      {showCupomModal && cupomEncontrado && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-300">
            {/* Confetti/Sparkles */}
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Gift className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Título */}
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <h3 className="text-xl font-bold text-gray-900">Parabéns!</h3>
                <Sparkles className="w-5 h-5 text-yellow-500" />
              </div>
              <p className="text-gray-600">
                Você ganhou um desconto especial!
              </p>
            </div>

            {/* Cupom */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-dashed border-green-300 rounded-xl p-4 mb-4">
              <p className="text-center text-sm text-green-700 mb-1">Cupom aplicado automaticamente</p>
              <p className="text-center text-2xl font-bold text-green-600 font-mono">
                {cupomEncontrado.codigo}
              </p>
              <p className="text-center text-lg font-semibold text-green-700 mt-1">
                {cupomEncontrado.desconto_percent}% OFF
              </p>
            </div>

            {/* Mensagem */}
            <p className="text-center text-sm text-gray-500 mb-4">
              Este desconto já foi adicionado ao seu cadastro. Continue para aproveitar!
            </p>

            {/* Botão */}
            <Button
              onClick={() => setShowCupomModal(false)}
              className="w-full rounded-xl h-11 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium"
            >
              Continuar cadastro 🎉
            </Button>

            {/* Fechar */}
            <button
              onClick={() => setShowCupomModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;
